import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";

export class StringImpulse extends StringModule {
  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "impulse";

    this.attributesToMap.push(
      { key: "position-strength", type: "number", fallback: this.settings["position-strength"] },
      { key: "position-tension", type: "number", fallback: this.settings["position-tension"] },
      { key: "position-friction", type: "number", fallback: this.settings["position-friction"] },
      {
        key: "position-max-velocity",
        type: "number",
        fallback: this.settings["position-max-velocity"],
      },
      {
        key: "position-update-threshold",
        type: "number",
        fallback: this.settings["position-update-threshold"],
      },

      { key: "rotation-strength", type: "number", fallback: this.settings["rotation-strength"] },
      { key: "rotation-tension", type: "number", fallback: this.settings["rotation-tension"] },
      { key: "rotation-friction", type: "number", fallback: this.settings["rotation-friction"] },
      {
        key: "rotation-max-angular-velocity",
        type: "number",
        fallback: this.settings["rotation-max-angular-velocity"],
      },
      { key: "rotation-max-angle", type: "number", fallback: this.settings["rotation-max-angle"] },
      {
        key: "rotation-update-threshold",
        type: "number",
        fallback: this.settings["rotation-update-threshold"],
      },

      { key: "max-offset", type: "number", fallback: this.settings["max-offset"] },
      { key: "sleep-epsilon", type: "number", fallback: this.settings["sleep-epsilon"] },
      { key: "continuous-push", type: "boolean", fallback: this.settings["continuous-push"] }
    );
  }

  override onObjectConnected(object: StringObject): void {
    super.onObjectConnected(object);

    object.setProperty("offset-x", 0);
    object.setProperty("offset-y", 0);
    object.setProperty("velocity-x", 0);
    object.setProperty("velocity-y", 0);

    object.setProperty("angle-deg", 0);
    object.setProperty("ang-vel-deg", 0);

    object.setProperty("__prev-css-x", 0);
    object.setProperty("__prev-css-y", 0);
    object.setProperty("__prev-css-rot", 0);

    object.setProperty("__push-latch", false);

    this.hover.track(object);
    this.centers.attach(object);
  }

  onObjectDisconnected(object: StringObject): void {
    this.hover.untrack(object);
    this.centers.detach(object);
  }

  onMouseMove(_e?: MouseEvent): void {
    const hoveredObjects = this.hover.activeObjects();
    if (hoveredObjects.length === 0) return;

    const cursorVelocityX = this.data.cursor.velocityX;
    const cursorVelocityY = this.data.cursor.velocityY;
    if (cursorVelocityX === 0 && cursorVelocityY === 0) return;

    const mouseX = this.data.cursor.targetX;
    const mouseY = this.data.cursor.targetY;

    for (let i = 0; i < hoveredObjects.length; i++) {
      const object = hoveredObjects[i];

      const positionStrength = object.getProperty<number>("position-strength") || 0;
      if (positionStrength !== 0) {
        const isContinuousPush = object.getProperty<boolean>("continuous-push") ?? true;
        const isLatched = object.getProperty<boolean>("__push-latch") === true;

        if (isContinuousPush || !isLatched) {
          let objectVelocityX = object.getProperty<number>("velocity-x") || 0;
          let objectVelocityY = object.getProperty<number>("velocity-y") || 0;
          objectVelocityX += cursorVelocityX * positionStrength;
          objectVelocityY += cursorVelocityY * positionStrength;
          object.setProperty("velocity-x", objectVelocityX);
          object.setProperty("velocity-y", objectVelocityY);
          if (!isContinuousPush) object.setProperty("__push-latch", true);
        }
      }

      const rotationStrength = object.getProperty<number>("rotation-strength") ?? 0.75;

      if (rotationStrength !== 0) {
        const { cx: centerX, cy: centerY } = this.centers.getCenter(object);
        const relativeX = mouseX - centerX;
        const relativeY = mouseY - centerY;
        const torque = relativeX * cursorVelocityY - relativeY * cursorVelocityX;

        let angularVelocityDeg = object.getProperty<number>("ang-vel-deg") || 0;
        angularVelocityDeg += torque * rotationStrength * 0.02;
        object.setProperty("ang-vel-deg", angularVelocityDeg);
      }
    }
  }

  onFrame(_: StringData): void {
    const hoveredSet = new Set(this.hover.activeObjects());

    for (let i = 0; i < this.objects.length; i++) {
      const objectItem = this.objects[i];
      if (!hoveredSet.has(objectItem) && objectItem.getProperty<boolean>("__push-latch") === true) {
        objectItem.setProperty("__push-latch", false);
      }
    }

    for (let i = 0; i < this.objects.length; i++) {
      const object = this.objects[i];

      let offsetX = object.getProperty<number>("offset-x") || 0;
      let offsetY = object.getProperty<number>("offset-y") || 0;
      let velocityX = object.getProperty<number>("velocity-x") || 0;
      let velocityY = object.getProperty<number>("velocity-y") || 0;

      const positionTension = object.getProperty<number>("position-tension") ?? 0.05;
      const positionFriction = object.getProperty<number>("position-friction") ?? 0.15;
      const maxVelocity = object.getProperty<number>("position-max-velocity") ?? 120;
      const maxOffset = object.getProperty<number>("max-offset") ?? 220;

      velocityX -= positionTension * offsetX;
      velocityY -= positionTension * offsetY;

      const velocityDecay = 1 - positionFriction;
      velocityX *= velocityDecay;
      velocityY *= velocityDecay;

      if (velocityX > maxVelocity) velocityX = maxVelocity;
      else if (velocityX < -maxVelocity) velocityX = -maxVelocity;
      if (velocityY > maxVelocity) velocityY = maxVelocity;
      else if (velocityY < -maxVelocity) velocityY = -maxVelocity;

      offsetX += velocityX;
      offsetY += velocityY;

      if (offsetX > maxOffset) offsetX = maxOffset;
      else if (offsetX < -maxOffset) offsetX = -maxOffset;
      if (offsetY > maxOffset) offsetY = maxOffset;
      else if (offsetY < -maxOffset) offsetY = -maxOffset;

      let angleDeg = object.getProperty<number>("angle-deg") || 0;
      let angularVelocityDeg = object.getProperty<number>("ang-vel-deg") || 0;

      const rotationTension = object.getProperty<number>("rotation-tension") ?? 0.06;
      const rotationFriction = object.getProperty<number>("rotation-friction") ?? 0.18;
      const maxAngularVelocityDeg =
        object.getProperty<number>("rotation-max-angular-velocity") ?? 6;
      const maxRotationAngleDeg = object.getProperty<number>("rotation-max-angle") ?? 18;

      angularVelocityDeg -= rotationTension * angleDeg;
      angularVelocityDeg *= 1 - rotationFriction;

      if (angularVelocityDeg > maxAngularVelocityDeg) angularVelocityDeg = maxAngularVelocityDeg;
      else if (angularVelocityDeg < -maxAngularVelocityDeg)
        angularVelocityDeg = -maxAngularVelocityDeg;

      angleDeg += angularVelocityDeg;

      if (angleDeg > maxRotationAngleDeg) {
        angleDeg = maxRotationAngleDeg;
        angularVelocityDeg *= 0.35;
      } else if (angleDeg < -maxRotationAngleDeg) {
        angleDeg = -maxRotationAngleDeg;
        angularVelocityDeg *= 0.35;
      }

      const sleepEpsilon = object.getProperty<number>("sleep-epsilon") ?? 0.01;
      const isPositionAsleep =
        velocityX * velocityX + velocityY * velocityY < sleepEpsilon * sleepEpsilon &&
        offsetX * offsetX + offsetY * offsetY < sleepEpsilon * sleepEpsilon;

      const isRotationAsleep =
        Math.abs(angularVelocityDeg) < sleepEpsilon && Math.abs(angleDeg) < sleepEpsilon;

      if (isPositionAsleep) {
        if (velocityX || velocityY || offsetX || offsetY) {
          object.setProperty("offset-x", 0);
          object.setProperty("offset-y", 0);
          object.setProperty("velocity-x", 0);
          object.setProperty("velocity-y", 0);
          offsetX = offsetY = velocityX = velocityY = 0;
        }
      } else {
        object.setProperty("offset-x", offsetX);
        object.setProperty("offset-y", offsetY);
        object.setProperty("velocity-x", velocityX);
        object.setProperty("velocity-y", velocityY);
      }

      if (isRotationAsleep) {
        if (angleDeg || angularVelocityDeg) {
          object.setProperty("angle-deg", 0);
          object.setProperty("ang-vel-deg", 0);
          angleDeg = angularVelocityDeg = 0;
        }
      } else {
        object.setProperty("angle-deg", angleDeg);
        object.setProperty("ang-vel-deg", angularVelocityDeg);
      }

      const positionUpdateThreshold =
        object.getProperty<number>("position-update-threshold") ?? 0.1;
      const rotationUpdateThreshold =
        object.getProperty<number>("rotation-update-threshold") ?? 0.15;

      const prevCssX = object.getProperty<number>("__prev-css-x") || 0;
      const prevCssY = object.getProperty<number>("__prev-css-y") || 0;
      const prevCssRot = object.getProperty<number>("__prev-css-rot") || 0;

      const cssTranslateX = Math.round(offsetX * 10) / 10;
      const cssTranslateY = Math.round(offsetY * 10) / 10;
      const cssRotateDeg = Math.round(angleDeg * 10) / 10;

      const needsPositionUpdate =
        Math.abs(cssTranslateX - prevCssX) > positionUpdateThreshold ||
        Math.abs(cssTranslateY - prevCssY) > positionUpdateThreshold;

      const needsRotationUpdate = Math.abs(cssRotateDeg - prevCssRot) > rotationUpdateThreshold;

      if (needsPositionUpdate || needsRotationUpdate) {
        this.applyToElementAndConnects(object, (el) => {
          if (needsPositionUpdate) {
            el.style.setProperty("--push-x", String(cssTranslateX));
            el.style.setProperty("--push-y", String(cssTranslateY));
          }
          if (needsRotationUpdate) {
            el.style.setProperty("--push-rotation", String(cssRotateDeg));
          }
        });

        if (needsPositionUpdate) {
          object.setProperty("__prev-css-x", cssTranslateX);
          object.setProperty("__prev-css-y", cssTranslateY);
        }
        if (needsRotationUpdate) {
          object.setProperty("__prev-css-rot", cssRotateDeg);
        }
      }
    }
  }
}
